import {
  INTAKE_ROW_ID_PATTERN, UNIFIED_QUESTIONS, isPlacedOn, unifiedQuestionById, validateUnifiedValue,
  type UnifiedForm, type UnifiedQuestion,
} from '@steward-app/steward-types';
import { newRowId } from './draft';

/**
 * The snapshot form's answers, as the unified client record holds them
 * (docs/43 §3.2, crossmarc/steward-app#2699 slice 4b), and the conversion to and from the
 * nested `sections` object the wizard's draft model already speaks.
 *
 * The wizard keeps its all-strings `IntakeDraft` and its `draftPayload` / `reviveDraft` pair. This
 * file is the one place that knows the record's shape, so those did not have to move:
 *
 *   - {@link sectionsToWire}: `sections` (what `draftPayload` produces) to `{ answers, lists }`,
 *     the body of `POST …/answers`. Scalars are filed under their unified id; a list is
 *     `lists[listId]`, each row `{ id, fields }` keyed by the item field's unified id
 *     (`cashAccounts[].institution`).
 *   - {@link wireToSections}: the record the server returned, back to `sections`.
 *   - {@link mergeStale}: the three-way merge a 409 `stale_save` needs.
 *
 * ## What a save means on the server
 *
 * Replace, scoped to the snapshot form (appcore `client-record-repository.ts`, read 2026-11-01):
 * a question placed on the snapshot form and ABSENT from the body is cleared, and a list's rows
 * ARE the list (a stored row the body omits is deleted). So {@link sectionsToWire} always builds
 * the WHOLE form, and an answer it cannot send (a value the unified validator refuses) is held at
 * the value the last read had, never left out, because leaving it out would clear a stored answer.
 *
 * ## Item ids
 *
 * A list row's id is what keeps the fields staff or another form wrote on that row. A row the
 * wizard gave a `rowId` (cash and investment accounts, #2185) uses it as its id. Any other row has
 * no id on the page, so {@link assignItemIds} matches it to the last read: the same content keeps
 * its id, then a row edited in place (same position, content changed) keeps the id at that
 * position, and only a genuinely new row is minted one. A removal above a row that is also edited
 * in the same save can hand it a neighbour's id; the cost is bounded to the fields another writer
 * placed on that one row. `snapshotAnswers.test.ts` pins the cases in both directions.
 */

/** A raw answer value as the routes carry it: the value alone, with no provenance. */
export type WireValue = string | boolean | string[];
export interface WireItem { id: string; fields: Record<string, WireValue> }
export interface WireAnswers {
  answers: Record<string, WireValue>;
  lists: Record<string, WireItem[]>;
}

export const emptyWire = (): WireAnswers => ({ answers: {}, lists: {} });

/** The snapshot's own questions: the ones the legacy snapshot schema also asks. */
const SNAPSHOT_QUESTIONS: readonly UnifiedQuestion[] = UNIFIED_QUESTIONS.filter(
  (q) => q.legacy.snapshot !== undefined && q.row === 'answers',
);
const SCALARS = SNAPSHOT_QUESTIONS.filter((q) => q.listId === undefined && q.valueType !== 'list');
const LISTS = SNAPSHOT_QUESTIONS.filter((q) => q.valueType === 'list');

/** The lists whose rows carry the wizard's own `rowId` (#2185), and so use it as their item id. */
const ROW_ID_LISTS: ReadonlySet<string> = new Set(['cashAccounts', 'investmentAccounts.accounts']);
const ROW_ID = new RegExp(INTAKE_ROW_ID_PATTERN);

const itemFieldKey = (id: string): string => id.slice(id.indexOf('[].') + 3);

function at(root: unknown, path: readonly string[]): unknown {
  let node: unknown = root;
  for (const step of path) {
    if (typeof node !== 'object' || node === null || Array.isArray(node)) return undefined;
    node = (node as Record<string, unknown>)[step];
  }
  return node;
}

function setAt(root: Record<string, unknown>, path: readonly string[], value: unknown): void {
  let node = root;
  for (const step of path.slice(0, -1)) {
    const next = node[step];
    if (typeof next === 'object' && next !== null && !Array.isArray(next)) {
      node = next as Record<string, unknown>;
    } else {
      const made: Record<string, unknown> = {};
      node[step] = made;
      node = made;
    }
  }
  node[path[path.length - 1]!] = value;
}

/** A value in the shape the question takes, or `undefined` for "no answer" (a clear). */
function typed(q: UnifiedQuestion, raw: unknown): WireValue | undefined {
  switch (q.valueType) {
    case 'boolean': return typeof raw === 'boolean' ? raw : undefined;
    case 'multiEnum': return Array.isArray(raw) && raw.length > 0 && raw.every((v) => typeof v === 'string') ? raw as string[] : undefined;
    case 'stringList': return undefined;
    default: return typeof raw === 'string' && raw.trim() !== '' ? raw : undefined;
  }
}

const sameValue = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

const sameFields = (a: Record<string, WireValue>, b: Record<string, WireValue>): boolean => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) if (!sameValue(a[k], b[k])) return false;
  return true;
};

/**
 * Gives every row of one list its id (see the file header). `rows` are the page's rows as item
 * field maps, `ownIds` the ids the page already holds for them (a `rowId`), and `stored` the list
 * the last read returned.
 */
export function assignItemIds(
  rows: readonly Record<string, WireValue>[],
  ownIds: readonly (string | undefined)[],
  stored: readonly WireItem[],
): string[] {
  const ids: (string | undefined)[] = rows.map((_, i) => ownIds[i]);
  const used = new Set<string>(ids.filter((id): id is string => id !== undefined));
  const free = (item: WireItem): boolean => !used.has(item.id);
  // 1. The same content keeps its id.
  rows.forEach((row, i) => {
    if (ids[i] !== undefined) return;
    const exact = stored.filter((s) => free(s) && sameFields(s.fields, row));
    const pick = exact.find((s) => stored[i]?.id === s.id) ?? exact[0];
    if (pick) { ids[i] = pick.id; used.add(pick.id); }
  });
  // 2. A row edited in place keeps the id at its position, unless that stored row is still
  //    another page row's exact content.
  rows.forEach((_, i) => {
    if (ids[i] !== undefined) return;
    const same = stored[i];
    if (same && free(same) && !rows.some((r, j) => j !== i && sameFields(same.fields, r))) {
      ids[i] = same.id; used.add(same.id);
    }
  });
  // 3. A new row is minted.
  return ids.map((id) => id ?? newRowId());
}

/**
 * `sections` (the wizard's save payload) to the body of `POST …/answers`, and the ids of what was
 * HELD BACK instead of sent: a scalar or a list field whose value fails the record's own
 * validator (held at its stored value), and a list with more rows than the record admits (the
 * whole list held at its stored rows). Nothing is ever silently cut: a non-empty `heldBack` means
 * the record does not hold what the page shows, so the caller must not report the save as saved.
 */
export interface WireSave { wire: WireAnswers; heldBack: string[] }

/**
 * One list's rows as wire items: each row gets its id ({@link assignItemIds}), and a field whose
 * value fails the record's validator is held at the value the matched stored row had (never left
 * out: that would clear it) with its id added to `heldBack`. A row left with no field is dropped.
 * Shared by the snapshot's and the onboarding form's converters, so the two cannot drift.
 */
export function wireList(
  rows: readonly Record<string, WireValue>[],
  ownIds: readonly (string | undefined)[],
  stored: readonly WireItem[],
  heldBack: Set<string>,
): WireItem[] {
  const ids = assignItemIds(rows, ownIds, stored);
  return rows.map((row, i) => {
    const fields = { ...row };
    const id = ids[i]!;
    const prior = stored.find((s) => s.id === id);
    for (const [fid, value] of Object.entries(fields)) {
      if (!validateUnifiedValue(fid, value).ok) {
        heldBack.add(fid);
        const held = prior?.fields[fid];
        if (held === undefined) delete fields[fid]; else fields[fid] = held;
      }
    }
    return { id, fields };
  }).filter((item) => Object.keys(item.fields).length > 0);
}

export function sectionsToWire(sections: Record<string, unknown>, stored: WireAnswers | null): WireSave {
  const base = stored ?? emptyWire();
  const wire = emptyWire();
  const heldBack = new Set<string>();

  for (const q of SCALARS) {
    const raw = at(sections, q.id.split('.'));
    let value = typed(q, raw);
    // The draft starts every yes/no at false, so a false is an answer only where the record
    // already holds one (a person who turned it back off). An untouched form sends none.
    if (q.valueType === 'boolean' && value === false && !(q.id in base.answers)) value = undefined;
    if (value === undefined) continue;
    if (validateUnifiedValue(q.id, value).ok) wire.answers[q.id] = value;
    else {
      heldBack.add(q.id);
      if (q.id in base.answers) wire.answers[q.id] = base.answers[q.id]!;
    }
  }

  for (const list of LISTS) {
    const rawRows = at(sections, list.id.split('.'));
    if (!Array.isArray(rawRows)) {
      // The section is absent from the payload (held out as unreadable): keep what is stored.
      if (base.lists[list.id]) wire.lists[list.id] = base.lists[list.id]!;
      continue;
    }
    if (list.maxItems !== undefined && rawRows.length > list.maxItems) {
      // More rows than the record admits. Sending a prefix would make the replace delete the
      // rest (and with a prepending list, the OLDEST stored row), so the list stays as stored.
      heldBack.add(list.id);
      if (base.lists[list.id]) wire.lists[list.id] = base.lists[list.id]!;
      continue;
    }
    const itemQs = SNAPSHOT_QUESTIONS.filter((q) => q.listId === list.id);
    const stored = base.lists[list.id] ?? [];
    const rows: Record<string, WireValue>[] = [];
    const ownIds: (string | undefined)[] = [];
    for (const raw of rawRows) {
      if (typeof raw !== 'object' || raw === null) continue;
      const obj = raw as Record<string, unknown>;
      const fields: Record<string, WireValue> = {};
      for (const q of itemQs) {
        const value = typed(q, obj[itemFieldKey(q.id)]);
        if (value !== undefined) fields[q.id] = value;
      }
      if (Object.keys(fields).length === 0) continue;
      rows.push(fields);
      const own = ROW_ID_LISTS.has(list.id) ? obj['rowId'] : undefined;
      ownIds.push(typeof own === 'string' && ROW_ID.test(own) ? own : undefined);
    }
    if (rows.length === 0 && stored.length === 0) continue;
    wire.lists[list.id] = wireList(rows, ownIds, stored, heldBack);
    if (wire.lists[list.id]!.length === 0) delete wire.lists[list.id];
  }
  return { wire, heldBack: [...heldBack] };
}

/** The record the server returned (`{ value }` answers) to raw wire values. Unknown ids are dropped. */
export function viewToWire(view: {
  answers?: Record<string, unknown>;
  lists?: Record<string, { id: string; fields: Record<string, unknown> }[]>;
}, form: UnifiedForm = 'snapshot'): WireAnswers {
  // The snapshot form takes the questions its legacy schema asks; the onboarding form takes the
  // ones placed on it. A view is already limited to the calling form's questions by the server.
  const onForm = (q: UnifiedQuestion): boolean => q.row === 'answers'
    && (form === 'snapshot' ? q.legacy.snapshot !== undefined : isPlacedOn(q.id, form));
  const wire = emptyWire();
  const valueOf = (a: unknown): WireValue | undefined => {
    const v = typeof a === 'object' && a !== null ? (a as Record<string, unknown>)['value'] : undefined;
    return typeof v === 'string' || typeof v === 'boolean' || (Array.isArray(v) && v.every((x) => typeof x === 'string'))
      ? v as WireValue : undefined;
  };
  for (const [id, a] of Object.entries(view.answers ?? {})) {
    const q = unifiedQuestionById(id);
    const v = valueOf(a);
    if (q && q.listId === undefined && q.valueType !== 'list' && onForm(q) && v !== undefined) wire.answers[id] = v;
  }
  for (const [listId, items] of Object.entries(view.lists ?? {})) {
    const listQ = unifiedQuestionById(listId);
    if (listQ?.valueType !== 'list' || !onForm(listQ) || !Array.isArray(items)) continue;
    wire.lists[listId] = items.map((item) => {
      const fields: Record<string, WireValue> = {};
      for (const [fid, a] of Object.entries(item.fields ?? {})) {
        const v = valueOf(a);
        if (v !== undefined && unifiedQuestionById(fid)?.listId === listId) fields[fid] = v;
      }
      return { id: item.id, fields };
    }).filter((item) => Object.keys(item.fields).length > 0);
  }
  return wire;
}

/** The record to the nested `sections` the wizard revives. The inverse of {@link sectionsToWire}. */
export function wireToSections(wire: WireAnswers): Record<string, unknown> {
  const sections: Record<string, unknown> = {};
  for (const [id, value] of Object.entries(wire.answers)) setAt(sections, id.split('.'), value);
  for (const [listId, items] of Object.entries(wire.lists)) {
    setAt(sections, listId.split('.'), items.map((item) => {
      const row: Record<string, unknown> = ROW_ID_LISTS.has(listId) ? { rowId: item.id } : {};
      for (const [fid, value] of Object.entries(item.fields)) row[itemFieldKey(fid)] = value;
      return row;
    }));
  }
  return sections;
}

/**
 * The three-way merge after a 409 `stale_save`: `sent` is what this tab was saving, `base` the
 * copy the PAGE is built on (the last read the page adopted, or the last save it made), `server`
 * the copy now. A read the page did not adopt is not a base: see `snapshotRecordApi.ts`. Anything the person changed since `base`
 * (an answer, a cleared answer, a whole list) stays theirs; anything they did not touch takes the
 * server's value. The unit is the answer for a scalar and the whole list for a list, so a list the
 * person edited is theirs entire. A person's unsaved edits are never discarded by a stale save.
 */
export function mergeStale(sent: WireAnswers, base: WireAnswers, server: WireAnswers): WireAnswers {
  const out = emptyWire();
  const keys = new Set([...Object.keys(sent.answers), ...Object.keys(base.answers), ...Object.keys(server.answers)]);
  for (const k of keys) {
    const edited = !sameValue(sent.answers[k], base.answers[k]);
    const v = edited ? sent.answers[k] : server.answers[k];
    if (v !== undefined) out.answers[k] = v;
  }
  const lists = new Set([...Object.keys(sent.lists), ...Object.keys(base.lists), ...Object.keys(server.lists)]);
  for (const l of lists) {
    const edited = !sameValue(sent.lists[l] ?? [], base.lists[l] ?? []);
    const v = edited ? sent.lists[l] : server.lists[l];
    if (v !== undefined && v.length > 0) out.lists[l] = v;
  }
  return out;
}

export const wireEqual = (a: WireAnswers, b: WireAnswers): boolean => sameValue(canonical(a), canonical(b));

function canonical(w: WireAnswers): unknown {
  const sortObj = (o: Record<string, unknown>) => Object.fromEntries(Object.entries(o).sort(([x], [y]) => x.localeCompare(y)));
  return {
    answers: sortObj(w.answers),
    lists: sortObj(Object.fromEntries(Object.entries(w.lists).map(([k, items]) => [k, items.map((i) => ({ id: i.id, fields: sortObj(i.fields) }))]))),
  };
}
